import { FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import type { Claim, ExpenseSummary } from '../../../../types/LegalCase';
import { computeCaseResult } from './caseResult';

interface CaseResultCardProps {
    claim: Claim | null;
    expensesSummary: ExpenseSummary | null | undefined;
}

// Cor da % de custo sobre o recuperável: quanto maior, mais alerta. ≥80% vermelho (custo come
// quase tudo), ≥50% âmbar, senão neutro. Fora do componente (sem função dentro de função).
const costRatioClass = (pct: number): string => {
    if (pct >= 80) return 'text-red-700 dark:text-red-300';
    if (pct >= 50) return 'text-amber-700 dark:text-amber-300';
    return 'text-gray-700 dark:text-gray-200';
};

// Card read-only no Resumo do caso: confronta o recuperável (o **valor atualizado** da dívida —
// saldo em aberto + juros e multa das taxas do caso + custas) com o custo incorrido (despesas) e
// mostra o resultado esperado. Junta duas seções distintas (Pretensão e Custos)
// numa visão executiva — "abri o caso e já sei se vale a pena". Não edita nada; a edição
// continua nas seções próprias. Não soma custo na pretensão: confronta (net = recuperável −
// custo). Renderiza o que existe mesmo com um dos lados faltando.
const CaseResultCard = ({ claim, expensesSummary }: CaseResultCardProps) => {
    const r = computeCaseResult(claim, expensesSummary);

    // Sem pretensão e sem custo não há o que mostrar (o resto do Resumo já cobre o caso).
    if (!r.hasData) return null;

    const positive = r.net >= 0;

    return (
        <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Resultado do caso
            </h4>
            <div className="rounded-lg bg-gray-50 dark:bg-slate-900 p-3 space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                    <span
                        className="text-gray-500 dark:text-gray-400"
                        title="Valor atualizado: saldo em aberto + juros e multa das taxas do caso + custas, já descontado o que foi pago."
                    >
                        Saldo a recuperar
                    </span>
                    <span className="dark:text-white">
                        {r.hasClaim ? r.recoverableFormatted : '—'}
                    </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Custo incorrido (despesas)</span>
                    <span className="text-red-600 dark:text-red-400">
                        {r.hasCost ? `− ${r.costFormatted}` : '—'}
                    </span>
                </div>
                {r.hasNet && (
                    <>
                        <div className="border-t border-gray-200 dark:border-slate-700 my-1" />
                        <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                                {positive
                                    ? <FiTrendingUp className="text-green-600 dark:text-green-400" size={14} />
                                    : <FiTrendingDown className="text-red-600 dark:text-red-400" size={14} />}
                                Resultado esperado
                            </span>
                            <span className={`font-semibold ${positive
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-red-700 dark:text-red-300'}`}>
                                {r.netFormatted}
                            </span>
                        </div>
                    </>
                )}
                {r.hasCostRatio && (
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 dark:text-gray-400">Custo sobre o recuperável</span>
                        <span className={`font-medium ${costRatioClass(r.costRatioPct)}`}>
                            {r.costRatioFormatted}
                        </span>
                    </div>
                )}
            </div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Confronto entre o que há a recuperar e o que o caso já custou. Depósito judicial
                levantado só entra quando o levantamento é abatido na pretensão.
            </p>
        </div>
    );
};

export default CaseResultCard;
